import { useState, useEffect, useCallback } from 'react';

// MUI Components
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';

// lucide Icons
import {
  BarChart4,
  Star,
  PlayCircle,
  Gamepad2,
  Check,
  Pencil,
  X,
} from 'lucide-react';

import { CloudUpload } from "@mui/icons-material"

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

import { fetchWrapperNoAuth, SERVER } from '../../utils/helpers';

// Components
import ProductForm from './ProductForm';
import GameDialog from './GameDialog';
import Leaderboard from './Leaderboard';
import GameHeader from './GameHeader';
import GameContainer from './GameContainer';
import GameCompletionHeader from './GameCompletionHeader';
import GameStatusHeader from './GameStatusHeader';
import GameNavigation from './GameNavigation';
import ProductImage from './ProductImage';
import ProgressBar from './ProgressBar';
import GameCSVUpload from './GameCSVUpload';


const ProductMetadataGame = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const { user, password } = useAuth();
  const [products, setProducts] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [selectedGameMode, setSelectedGameMode] = useState(["source", "dimensions", "cost", "missing"]);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [completedProductsIndex, setCompletedProductsIndex] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackColor, setFeedbackColor] = useState('success');
  const [showCloseAlert, setShowCloseAlert] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [localNickname, setLocalNickname] = useState('Anonymous');
  const [showCSV, setShowCSV] = useState(false);
  const currentProduct = products[currentProductIndex] || {};
  const requiredFields = ['cost', 'qty', 'source', 'length', 'width', 'height', 'weight'];


  const showTemporaryFeedback = useCallback((message, duration = 2000, color = "success") => {
    if (window.feedbackTimeout) {
      clearTimeout(window.feedbackTimeout);
      window.feedbackTimeout = null;
    }

    setShowFeedback(false);

    setTimeout(() => {
      setFeedback(message);
      setFeedbackColor(color);
      setShowFeedback(true);

      window.feedbackTimeout = setTimeout(() => {
        setShowFeedback(false);
        window.feedbackTimeout = null;
      }, duration);
    }, 100);
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!user || !password) return;
    setLoadingProducts(true);
    try {
      const res = await fetchWrapperNoAuth(`${SERVER}/reports/game?mode=${selectedGameMode.join(',')}`, user, password)
      setProducts(res || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      showTemporaryFeedback('Failed to load products. Please try again.', 2000, "warning");
    } finally {
      setLoadingProducts(false);
    }
  }, [user, password, showTemporaryFeedback, selectedGameMode]);

  const fetchLeaderboard = useCallback(async () => {
    if (!user || !password) return;

    setLoadingLeaderboard(true);
    try {
      const res = await fetchWrapperNoAuth(`${SERVER}/reports/game/leaderboard`, user, password);
      setLocalNickname(res[0])
      res.splice(0, 1);
      setLeaderboardData(res || []);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      showTemporaryFeedback('Failed to load leaderboard. Please try again.', 2000, "warning");
    } finally {
      setLoadingLeaderboard(false);
    }
  }, [user, password, showTemporaryFeedback]);


  useEffect(() => {
    fetchLeaderboard();
    fetchProducts();
  }, [fetchLeaderboard, fetchProducts]);


  useEffect(() => {
    if (gameStarted) {
      setProgressPercentage(((currentProductIndex) / products.length) * 100);
    }
  }, [currentProductIndex, products.length, gameStarted]);

  useEffect(() => {
    if (gameStarted) {
      setProgressPercentage(((currentProductIndex) / products.length) * 100);
    }
  }, [currentProductIndex, products.length, gameStarted]);


  const handleChange = (field, value) => {
    setProducts(prev => prev.map((product, idx) =>
      idx === currentProductIndex
        ? { ...product, [field]: value }
        : product
    ));
  };

  const isCurrentProductComplete = () => {
    return requiredFields.every(field => {
      const value = currentProduct[field];
      if (field === 'cost' || field === 'qty' || field === 'length' || field === 'width' || field === 'height' || field === 'weight') {
        return value !== null && value !== '' && !isNaN(value) && value > 0;
      }
      return value !== null && value !== '';
    });
  };


  const handleNext = () => {
    if (!isCurrentProductComplete()) {
      showTemporaryFeedback('Please fill in all required fields first!', 2000, "warning");
      return;
    }

    if (!completedProductsIndex.includes(currentProductIndex)) {
      setScore(score + 10);
      setCompletedProductsIndex(prev => [...prev, currentProductIndex]);
    }

    setCurrentProductIndex(currentProductIndex + 1);
  };


  const finishGame = useCallback(() => {
    setGameCompleted(true);
    if (products.length > 0) {
      setFeedback(`Game completed! You scored ${score} points.`, 2000, "success");
      let productsFilled = []
      completedProductsIndex.forEach(index => {
        productsFilled.push(products[index])
      });
      if (productsFilled.length === 0) return;
      fetchWrapperNoAuth(`${SERVER}/reports/game`, user, password,
        {
          method: "POST",
          body: { products: productsFilled }
        }).catch(error => {
          alert("Unable to save your data. Please contact support for assistance.");
        });
    }
  }, [products, score, completedProductsIndex, user, password]);

  const handleSkip = () => {
    if (currentProductIndex < products.length - 1) {
      setCurrentProductIndex(currentIndex => currentIndex + 1);
      showTemporaryFeedback('Product skipped', 2000, "info");
    } else {
      finishGame();
    }
  };

  const showDialogCloseWarning = () => {
    if (gameStarted && !gameCompleted) {
      setShowCloseAlert(true);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (gameStarted && currentProductIndex >= products.length && products.length > 0) {
      finishGame();
    }
  }, [currentProductIndex, gameStarted, products.length, finishGame]);

  if (!isOpen) return null;

  const mainBoxSx = {
    bgcolor: theme.background.primary,
    borderRadius: 3,
    boxShadow: 6,
    p: { xs: 2, sm: 3 },
    maxWidth: 600,
    mx: 'auto',
    position: 'relative',
    minWidth: { xs: '90vw', sm: 550 },
    maxHeight: '80vh',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '10px',
      height: '10px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.background.primary,
      borderRadius: '15px',
      border: `1px solid ${theme.border.light}`,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.scrollbar,
      borderRadius: '15px',
      cursor: 'grab',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: theme.scrollbarHover,
    },
    '&::-webkit-scrollbar-button': {
      display: 'none',
    },
  };

  if (showCSV) {
    return (
      <GameContainer>
        <GameHeader onClose={() => setShowCSV(false)} showBack={true} onBack={() => { setShowCSV(false) }} />
        <Typography variant="h5" color={theme.color.primary} fontWeight={700} mb={3} align="center" fontFamily={theme.fontFamily}>
          Upload the CSV file
        </Typography>
        <GameCSVUpload />
        <GameDialog
          open={showCloseAlert}
          title="Are you sure?"
          message="All progress will be lost!"
          onConfirm={() => { setShowCloseAlert(false); onClose(); }}
          onCancel={() => setShowCloseAlert(false)}
        />
      </GameContainer>
    )
  }

  if (leaderboardOpen) {
    return (
      <GameContainer>
        <GameHeader onClose={() => setLeaderboardOpen(false)} showBack={true} onBack={() => { setLeaderboardOpen(false) }} />
        <Typography variant="h5" color={theme.color.primary} fontWeight={700} mb={3} align="center" fontFamily={theme.fontFamily}>
          Leaderboard
        </Typography>
        <Leaderboard data={leaderboardData} loading={loadingLeaderboard} localNickname={localNickname} />
        <GameDialog
          open={showCloseAlert}
          title="Are you sure?"
          message="All progress will be lost!"
          onConfirm={() => { setShowCloseAlert(false); onClose(); }}
          onCancel={() => setShowCloseAlert(false)}
        />
      </GameContainer>
    );
  }


  if (!gameStarted) {
    return (
      <GameContainer>
        <GameHeader onClose={showDialogCloseWarning} showClose={true} />
        <Typography variant="h4" color={theme.color.primary} fontWeight={700} mb={2} align="center" sx={{ fontSize: '1.75rem', fontFamily: theme.fontFamily }}>
          Add Missing Info to Your Reports
        </Typography>
        <Typography variant="body1" color={theme.text.primary} mb={3} align="center" sx={{ maxWidth: '90%', mx: 'auto', fontFamily: theme.fontFamily }}>
          Enter product details for 10 products and earn points!
        </Typography>
        <Button
          onClick={() => { setGameStarted(true); }}
          disabled={loadingProducts || products.length === 0}
          variant="contained"
          color="primary"
          fullWidth
          sx={{
            py: 1.5,
            fontWeight: 600,
            fontFamily: theme.fontFamily,
            borderRadius: 2,
            mt: 2,
            boxShadow: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': !loadingProducts && products.length > 0 && {
              boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
              transform: 'translateY(-2px)',
              background: `${theme.color.primary}e8`
            },
            position: 'relative',
            overflow: 'hidden',
            minHeight: '48px'
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%'
          }}>
            {loadingProducts ? (
              <CircularProgress
                size={16}
                sx={{
                  color: 'white',
                  mr: 1.5,
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  },
                  animation: 'spin 1.2s linear infinite'
                }}
              />
            ) : (
              <Gamepad2 size={20} style={{
                marginRight: '10px',
                transition: 'transform 0.2s ease',
              }} />
            )}
            <Typography component="span" sx={{ fontWeight: 600, fontFamily: theme.fontFamily }}>
              {loadingProducts ? 'Loading game' : 'Start Game'}
            </Typography>
          </Box>
        </Button>
        <Button
          onClick={() => setShowCSV(true)}
          variant="contained"
          color="primary"
          fullWidth
          sx={{
            py: 1.5,
            fontWeight: 600,
            fontFamily: theme.fontFamily,
            borderRadius: 2,
            mt: 2,
            boxShadow: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
              transform: 'translateY(-2px)',
              background: `${theme.color.primary}e8`
            },
            position: 'relative',
            overflow: 'hidden',
            minHeight: '48px'
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%'
          }}>
            <CloudUpload size={20} style={{
              marginRight: '10px',
              transition: 'transform 0.2s ease',
            }} />
            <Typography component="span" sx={{ fontWeight: 600, fontFamily: theme.fontFamily }}>
              Upload CSV
            </Typography>
          </Box>
        </Button>
        <Button
          onClick={() => !loadingLeaderboard && setLeaderboardOpen(true)}
          variant="outlined"
          color="secondary"
          fullWidth
          disabled={loadingLeaderboard || leaderboardData.length === 0}
          sx={{
            mt: 2,
            py: 1.2,
            fontWeight: 600,
            fontFamily: theme.fontFamily,
            borderRadius: 2,
            borderWidth: 1.5,
            transition: 'all 0.2s ease-in-out',
            '&:hover': !loadingLeaderboard && {
              borderWidth: 1.5,
              background: `${theme.color.secondary}15`,
              transform: 'translateY(-1px)',
              boxShadow: '0 3px 8px rgba(0,0,0,0.08)'
            },
            position: 'relative',
            overflow: 'hidden',
            minHeight: '42px'
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%'
          }}>
            {loadingLeaderboard ? (
              <CircularProgress
                size={16}
                sx={{
                  color: theme.color.secondary,
                  mr: 1.5,
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  },
                  animation: 'spin 1.2s linear infinite'
                }}
              />
            ) : (
              <BarChart4 size={18} style={{
                marginRight: '8px',
                opacity: 0.9,
                transition: 'transform 0.2s ease'
              }} />
            )}
            <Typography component="span" sx={{ fontWeight: 600, fontFamily: theme.fontFamily }}>
              {loadingLeaderboard ? 'Loading leaderboard' : 'Leaderboard'}
            </Typography>
            {loadingLeaderboard}
          </Box>
        </Button>
        <Snackbar
          open={showFeedback}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={() => {
            if (window.feedbackTimeout) {
              clearTimeout(window.feedbackTimeout);
              window.feedbackTimeout = null;
            }
            setShowFeedback(false);
          }}
        >
          <Alert severity={feedbackColor} sx={{ width: '100%', fontFamily: theme.fontFamily }}>{feedback}</Alert>
        </Snackbar>
      </GameContainer>
    );
  }

  if (gameCompleted) {
    return (
      <GameContainer extendedWidth>
        <Snackbar
          open={showFeedback}
          autoHideDuration={2000}
          onClose={() => setShowFeedback(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={feedbackColor} sx={{ width: '100%', fontFamily: theme.fontFamily }}>{feedback}</Alert>
        </Snackbar>
        <GameHeader onClose={showDialogCloseWarning} showClose={true} />
        <GameCompletionHeader score={score} />
        <Box mb={3} sx={{
          background: score > 0
            ? `linear-gradient(45deg, ${theme.accent.success}10, ${theme.accent.success}30)`
            : `linear-gradient(45deg, ${theme.accent.error}10, ${theme.accent.error}30)`,
          borderRadius: 3,
          p: 2.5,
          border: score > 0 ? `1px solid ${theme.accent.success}40` : `1px solid ${theme.accent.error}40`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <Typography variant="h5" color={theme.text.primary} fontWeight={700} align="center" sx={{ mb: 1 }} fontFamily={theme.fontFamily}>
            {score > 0 ? `Final Score: ${score} points` : `No points earned`}
          </Typography>

          <Typography variant="body1" color={theme.text.secondary} align="center" fontFamily={theme.fontFamily}>
            {score > 0 ? 'All product metadata has been collected successfully!' : 'No valid product metadata was collected.'}
          </Typography>
          {score > 0 && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              {Array.from({ length: Math.min(5, Math.ceil(score / 10)) }).map((_, i) => (
                <Star key={i} size={24} fill={theme.accent.success} stroke={theme.accent.success} />
              ))}
            </Box>
          )}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Nickname"
              value={localNickname}
              disabled={!isEditingNickname}
              onChange={(e) => setLocalNickname(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={async () => {
                      if (isEditingNickname) {
                        if (!localNickname.trim()) {
                          showTemporaryFeedback('Nickname cannot be empty.', 2000, "warning");
                          return;
                        }
                        if (localNickname.length < 3 || localNickname.length > 16) {
                          showTemporaryFeedback('Nickname must be between 3 and 16 characters.', 2000, "warning");
                          return;
                        }
                        if (localNickname.includes(' ')) {
                          showTemporaryFeedback('Nickname cannot contain spaces.', 2000, "warning");
                          return;
                        }
                        const res = await fetchWrapperNoAuth(`${SERVER}/reports/game/nickname`, user, password, {
                          method: "PUT",
                          body: { nickname: localNickname }
                        })
                        if (res) {
                          showTemporaryFeedback('Nickname updated successfully!', 2000, "success");
                          setIsEditingNickname(!isEditingNickname);
                        }
                      }
                      else {
                        setIsEditingNickname(!isEditingNickname);
                      }
                    }}
                    sx={{
                      color: isEditingNickname ? theme.color.primary : theme.text.tertiary,
                      '&:hover': {
                        color: theme.color.primary
                      }
                    }}
                  >
                    {isEditingNickname ? <Check /> : <Pencil />}
                  </IconButton>
                )
              }}
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 2 }}>
          <Button
            onClick={() => {
              setGameStarted(false);
              setGameCompleted(false);
              setCurrentProductIndex(0);
              setScore(0);
              fetchProducts();
              fetchLeaderboard();
              setCompletedProductsIndex([]);
            }}
            variant="contained"
            color="primary"
            startIcon={<PlayCircle size={18} />}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontFamily: theme.fontFamily,
              borderRadius: 2,
              py: 1.2,
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 10px rgba(0,0,0,0.1)'
              }
            }}
          >
            Play Again
          </Button>
          <Button
            onClick={() => onClose()}
            variant="outlined"
            color="error"
            startIcon={<X size={18} />}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontFamily: theme.fontFamily,
              borderRadius: 2,
              py: 1.2,
              borderWidth: 1.5,
              transition: 'all 0.2s',
              '&:hover': {
                borderWidth: 1.5,
                transform: 'translateY(-2px)',
                bgcolor: `${theme.accent.error}10`,
                borderColor: theme.accent.error
              }
            }}
          >
            Close Game
          </Button>
        </Box>
        <GameDialog
          open={showCloseAlert}
          title="Are you sure?"
          message="All progress will be lost!"
          onConfirm={() => { setShowCloseAlert(false); onClose(); }}
          onCancel={() => setShowCloseAlert(false)}
        />
      </GameContainer>
    );
  }

  return (
    <Paper elevation={8} sx={mainBoxSx}>
      <GameHeader onClose={showDialogCloseWarning} showClose={true} />
      <GameStatusHeader score={score} currentIndex={currentProductIndex} totalProducts={products.length} />
      <ProgressBar percentage={progressPercentage} />
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3} mb={3}>
        <ProductImage src={currentProduct.image} alt={currentProduct.name || currentProduct.product_name} />
        <Box flex={2}>
          <ProductForm product={currentProduct} onChange={handleChange} />
        </Box>
      </Box>
      <GameNavigation
        currentIndex={currentProductIndex}
        totalProducts={products.length}
        onPrevious={() => currentProductIndex > 0 && setCurrentProductIndex(currentProductIndex - 1)}
        onNext={handleNext}
        onSkip={handleSkip}
        isComplete={isCurrentProductComplete()}
      />
      <Snackbar
        open={showFeedback}
        autoHideDuration={2000}
        onClose={() => setShowFeedback(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          position: 'fixed',
          zIndex: 9999,
          bottom: '24px !important'
        }}
      >
        <Alert
          severity={feedbackColor}
          sx={{
            width: '100%',
            fontFamily: theme.fontFamily,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }}
        >
          {feedback}
        </Alert>
      </Snackbar>
      <GameDialog
        open={showCloseAlert}
        title="Are you sure?"
        message="All progress will be lost!"
        onConfirm={() => { setShowCloseAlert(false); onClose(); }}
        onCancel={() => setShowCloseAlert(false)}
      />
    </Paper>
  );
};

export default ProductMetadataGame;